import styled, { CSSObject } from '@emotion/styled';
import { alpha, IconButton } from '@mui/material';

export const Section = styled.div(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  }),
);

export const TitleRow = styled.div(
  (): CSSObject => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
  }),
);

export const CountPill = styled.span(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  background: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 999,
  padding: '4px 10px',
  fontVariantNumeric: 'tabular-nums',
}));

export const VisitorList = styled.div(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  }),
);

export const VisitorCard = styled.article(({ theme }) => ({
  background: 'transparent',
  border: `1px solid ${alpha(theme.palette.text.primary, 0.14)}`,
  borderRadius: 12,
  padding: '1.25rem 1.25rem 1.5rem',
  transition: 'border-color 180ms ease, box-shadow 220ms ease',
  '&:hover': {
    borderColor: alpha(theme.palette.text.primary, 0.24),
  },
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.15)}`,
  },
}));

export const CardHead = styled.div(
  (): CSSObject => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
    marginBottom: '1rem',
  }),
);

export const CardHeadLeft = styled.div(
  (): CSSObject => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    minWidth: 0,
  }),
);

export const IndexChip = styled.span(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.8125rem',
  color: theme.palette.primary.dark,
  background: alpha(theme.palette.primary.main, 0.1),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
  borderRadius: 8,
  height: 28,
  minWidth: 36,
  padding: '0 9px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontVariantNumeric: 'tabular-nums',
  flexShrink: 0,
}));

export const RemoveButton = styled(IconButton)(({ theme }) => ({
  width: 34,
  height: 34,
  borderRadius: 10,
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.error.main,
    background: alpha(theme.palette.error.main, 0.08),
  },
  '&.Mui-disabled': {
    opacity: 0.35,
  },
}));

export const SupplierText = styled.div(({ theme }) => ({
  fontSize: '0.8125rem',
  color: theme.palette.text.secondary,
  marginBottom: '0.75rem',
}));

export const AddArea = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: '0.25rem',
  '&:hover .add-btn, &:has(.add-btn:focus-visible) .add-btn': {
    background: alpha(theme.palette.primary.main, 0.06),
    borderColor: theme.palette.primary.main,
    borderStyle: 'solid',
    color: theme.palette.primary.dark,
  },
  '&:hover .plus-badge, &:has(.add-btn:focus-visible) .plus-badge': {
    transform: 'rotate(90deg)',
  },
}));

export const AddButton = styled.button(({ theme }) => ({
  width: '100%',
  appearance: 'none',
  background: 'transparent',
  border: `1.5px dashed ${alpha(theme.palette.text.primary, 0.18)}`,
  borderRadius: 12,
  padding: '14px 18px',
  fontFamily: 'inherit',
  fontSize: '0.9375rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.625rem',
  cursor: 'pointer',
  transition: 'background 180ms ease, border-color 180ms ease, color 180ms ease',
  '&:focus-visible': {
    outline: 'none',
  },
  '& .plus-badge': {
    transition: 'transform 220ms ease',
  },
}));

export const PlusBadge = styled.span(({ theme }) => ({
  width: 24,
  height: 24,
  borderRadius: '50%',
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': { fontSize: 16 },
}));
